import "@/styles/tk-home.css";
import { useMemo, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  CalendarDays,
  Check,
  CircleCheck,
  Copy,
  Eye,
  Globe,
  Hourglass,
  Image as ImageIcon,
  Link2,
  MapPin,
  MessageSquare,
  Minus,
  Music,
  Plus,
  QrCode,
  Send,
  Smartphone,
  type LucideIcon,
} from "lucide-react";
import { TEMPLATES, CATEGORIES, formatPrice, type Template } from "@/lib/data";
import { InvitationCard } from "@/components/InvitationCard";
import { ArchFrame, Eyebrow, Sparkle } from "@/components/tk";
import { useT } from "@/lib/i18n";
import { brandHost, brandName, isIntlSite } from "@/lib/market";
import { kebab } from "@/lib/slug";
import { useHiddenIds, useVisibleCategoryCounts } from "@/lib/templateOverrides";

type T = (key: string, fallback?: string) => string;

// Public-catalog list: TEMPLATES minus `unlisted` (staged) templates, newest
// first. TEMPLATES stays the source of truth for resolving published invitations.
const LISTED_TEMPLATES: Template[] = TEMPLATES.filter((t) => !t.unlisted);

const PRICE = Math.min(...LISTED_TEMPLATES.map((t) => t.price));
const PRICE_VARIES = LISTED_TEMPLATES.some((t) => t.price !== PRICE);

const TELEGRAM_URL = "https://t.me/taklifla_bot";

// Hero arch row, left → right: index into the hero set + arch size.
const HERO_ORDER: Array<[number, "sm" | "md" | "lg"]> = [
  [3, "sm"],
  [1, "md"],
  [0, "lg"],
  [2, "md"],
  [4, "sm"],
];

const FEATURES: Array<{ key: string; Icon: LucideIcon }> = [
  { key: "link", Icon: Link2 },
  { key: "rsvp", Icon: CircleCheck },
  { key: "countdown", Icon: Hourglass },
  { key: "map", Icon: MapPin },
  { key: "music", Icon: Music },
  { key: "photos", Icon: ImageIcon },
  { key: "wishes", Icon: MessageSquare },
  { key: "qr", Icon: QrCode },
];

const FAQ: Array<{ q: string; a: string; aIntl?: string }> = [
  { q: "tk.home.faq.open.q", a: "tk.home.faq.open.a" },
  { q: "help.faq.edit.q", a: "help.faq.edit.a" },
  { q: "tk.home.faq.pay.q", a: "tk.home.faq.pay.a", aIntl: "tk.home.faq.pay.a_intl" },
  { q: "help.faq.share.q", a: "help.faq.share.a" },
  { q: "help.faq.lifetime.q", a: "help.faq.lifetime.a" },
];

// Sample figures for the illustrative owner-dashboard card (yes + no + maybe = total).
const DEMO_RSVP = { yes: 51, no: 7, maybe: 4 };
const DEMO_GUESTS: Array<[string, "yes" | "no"]> = [
  ["g1", "yes"],
  ["g2", "yes"],
  ["g3", "no"],
  ["g4", "yes"],
];

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();

const fill = (s: string, n: number | string) => s.replace("{n}", String(n));

export function LandingPage() {
  const t = useT();
  const hidden = useHiddenIds();
  const visibleCounts = useVisibleCategoryCounts();
  const visible = useMemo(
    () => LISTED_TEMPLATES.filter((tpl) => !hidden.has(tpl.id)),
    [hidden],
  );
  const hero = visible.slice(0, 5);
  const featured = hero[0] ?? LISTED_TEMPLATES[0];

  return (
    <div className="tk-home">
      <Hero t={t} hero={hero} featured={featured} />
      <Showcase t={t} visible={visible} hero={hero} counts={visibleCounts} />
      <HowItWorks t={t} hero={hero} featured={featured} />
      <Features t={t} />
      <Cabinet t={t} featured={featured} />
      <Pricing t={t} />
      <Faq t={t} />
      <FinalCta t={t} />
    </div>
  );
}

function SectionHead({
  eyebrow,
  title,
  lead,
  align = "center",
}: {
  eyebrow: ReactNode;
  title: ReactNode;
  lead?: ReactNode;
  align?: "center" | "start";
}) {
  return (
    <header className={`tk-home-head tk-home-head--${align}`}>
      <Eyebrow sparkles="start">{eyebrow}</Eyebrow>
      <h2 className="tk-h2 tk-home-head__title">{title}</h2>
      {lead && <p className="tk-lead tk-home-head__lead">{lead}</p>}
    </header>
  );
}

function Hero({ t, hero, featured }: { t: T; hero: Template[]; featured: Template }) {
  return (
    <section className="tk-home-hero">
      <div className="tk-container tk-home-hero__inner">
        <Eyebrow className="tk-desktop-only">{t("tk.home.hero.eyebrow")}</Eyebrow>
        <Eyebrow className="tk-mobile-only">{t("tk.home.hero.eyebrow_short")}</Eyebrow>
        <h1 className="tk-h1 tk-home-hero__title">
          {t("tk.home.hero.title_a")}
          <br className="tk-home-hero__br" /> <em>{t("tk.home.hero.title_em")}</em>{" "}
          {t("tk.home.hero.title_b")}
        </h1>
        <p className="tk-lead tk-home-hero__lead">{t("tk.home.hero.lead")}</p>
        <div className="tk-home-hero__ctas">
          <Link to="/templates" className="tk-btn tk-btn--primary tk-btn--lg">
            {t("tk.home.cta.choose")}
            <ArrowRight size={17} strokeWidth={1.8} aria-hidden="true" />
          </Link>
          <Link to={`/i/${featured.id}`} className="tk-btn tk-btn--secondary tk-btn--lg">
            <Eye size={18} strokeWidth={1.6} className="tk-home-anor" aria-hidden="true" />
            {t("tk.home.cta.demo")}
          </Link>
        </div>

        <div className="tk-home-hero__arches">
          {HERO_ORDER.map(([idx, size]) => {
            const tpl = hero[idx];
            if (!tpl) return null;
            return (
              <figure key={tpl.id} className={`tk-home-hero__fig tk-home-hero__fig--${size}`}>
                <Link to="/templates" aria-label={t("nav.gallery")} className="tk-home-hero__link">
                  <ArchFrame size={size === "lg" ? "md" : "sm"}>
                    <InvitationCard template={tpl} />
                  </ArchFrame>
                </Link>
                <figcaption>{tpl.title}</figcaption>
              </figure>
            );
          })}
        </div>

        <ul className="tk-home-trust">
          <li>
            <Smartphone size={20} strokeWidth={1.5} aria-hidden="true" />
            {t("tk.home.trust.devices")}
          </li>
          <li aria-hidden="true" className="tk-home-trust__sep"><Sparkle size={7} /></li>
          <li>
            <Link2 size={20} strokeWidth={1.5} aria-hidden="true" />
            {t("tk.home.trust.noapp")}
          </li>
          <li aria-hidden="true" className="tk-home-trust__sep"><Sparkle size={7} /></li>
          <li>
            <Globe size={20} strokeWidth={1.5} aria-hidden="true" />
            {t("tk.home.trust.langs")}
          </li>
        </ul>
      </div>
    </section>
  );
}

function Showcase({
  t,
  visible,
  hero,
  counts,
}: {
  t: T;
  visible: Template[];
  hero: Template[];
  counts: Map<string, number>;
}) {
  const [cat, setCat] = useState<string>("all");
  const cats = useMemo(
    () => CATEGORIES.filter((c) => c.id === "all" || (counts.get(c.id) ?? 0) > 0),
    [counts],
  );
  const active = cats.some((c) => c.id === cat) ? cat : "all";
  const shown = useMemo(() => {
    const pool =
      active === "all"
        ? visible.filter((tpl) => !hero.includes(tpl))
        : visible.filter((tpl) => tpl.category === active);
    return pool.slice(0, 8);
  }, [active, visible, hero]);
  const catLabel = (id: string) => {
    const c = CATEGORIES.find((x) => x.id === id);
    return c ? t(c.labelKey) : "";
  };

  return (
    <section className="tk-section tk-section--linen tk-home-show">
      <div className="tk-container">
        <SectionHead
          eyebrow={t("nav.gallery")}
          title={
            <>
              {t("tk.home.templates.title_a")} <em>{t("tk.home.templates.title_em")}</em>
            </>
          }
          lead={t("tk.home.templates.lead")}
        />
        <div className="tk-chips tk-home-chips" role="group" aria-label={t("tk.menu.types")}>
          {cats.map((c) => (
            <button
              key={c.id}
              type="button"
              className="tk-chip"
              aria-pressed={active === c.id}
              onClick={() => setCat(c.id)}
            >
              {t(c.labelKey)}
            </button>
          ))}
        </div>
        <div className="tk-home-grid">
          {shown.map((tpl) => (
            <Link key={tpl.id} to={`/templates/${tpl.id}`} className="tk-home-card">
              <ArchFrame>
                <InvitationCard template={tpl} />
              </ArchFrame>
              <div className="tk-home-card__meta">
                <h3 className="tk-home-card__title">{tpl.title}</h3>
                <span className="tk-home-card__price">{formatPrice(tpl.price)}</span>
                <span className="tk-home-card__cat">{catLabel(tpl.category)}</span>
              </div>
            </Link>
          ))}
        </div>
        <div className="tk-home-show__more">
          <Link
            to={active === "all" ? "/templates" : `/shablonlar?cat=${active}`}
            className="tk-btn tk-btn--secondary"
          >
            {fill(t("tk.home.templates.all"), counts.get(active) ?? 0)}
            <ArrowRight size={17} strokeWidth={1.8} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}

function ShareArt({ t, featured }: { t: T; featured: Template }) {
  const guest = t("tk.home.demo.g1.name");
  return (
    <div className="tk-home-art-card tk-home-art-share">
      <div className="tk-home-art-url">
        <span>
          <span className="tk-muted">{brandHost()}/k7m2x/</span>
          {kebab(featured.names)}
        </span>
        <Copy size={16} strokeWidth={1.5} />
      </div>
      <div className="tk-home-art-send">
        <Send size={16} strokeWidth={1.7} />
        {t("tk.home.how.art.send")}
      </div>
      <div className="tk-home-art-reply">
        <span className="tk-avatar tk-home-art-reply__avatar">{initials(guest)}</span>
        <span className="tk-home-art-reply__text">
          <strong>{guest.split(/\s+/)[0]}</strong> {t("tk.home.how.art.replied")}
        </span>
        <span className="tk-badge tk-badge--yes">{t("tk.home.demo.badge_yes")}</span>
      </div>
    </div>
  );
}

function HowItWorks({ t, hero, featured }: { t: T; hero: Template[]; featured: Template }) {
  const pick = [hero[1], hero[0], hero[2]];
  const arts: ReactNode[] = [
    <div className="tk-home-art-pick">
      {pick.map((tpl, i) =>
        tpl ? (
          <div key={tpl.id} className={i === 1 ? "tk-home-art-pick__on" : "tk-home-art-pick__side"}>
            <ArchFrame size="sm">
              <InvitationCard template={tpl} />
            </ArchFrame>
            {i === 1 && (
              <span className="tk-home-art-pick__check">
                <Check size={14} strokeWidth={2.4} />
              </span>
            )}
          </div>
        ) : null,
      )}
    </div>,
    <div className="tk-home-art-card tk-home-art-form">
      <span className="tk-home-art-label">{t("editor.names")}</span>
      <span className="tk-home-art-input is-focus">
        {featured.names}
        <span className="tk-home-art-caret" />
      </span>
      <span className="tk-home-art-label">{t("editor.date")}</span>
      <span className="tk-home-art-input">
        {featured.date}
        <CalendarDays size={18} strokeWidth={1.5} />
      </span>
    </div>,
    <ShareArt t={t} featured={featured} />,
  ];

  return (
    <section id="how" className="tk-section tk-home-how">
      <div id="how-it-works" className="tk-container">
        <SectionHead
          eyebrow={t("landing.cta.how")}
          title={
            <>
              {t("tk.home.how.title_a")} <em>{t("tk.home.how.title_em")}</em>
            </>
          }
          lead={t("tk.home.how.lead")}
        />
        <ol className="tk-home-steps">
          {[1, 2, 3].map((n, i) => (
            <li key={n} className="tk-home-step">
              <div className="tk-home-step__art" aria-hidden="true">
                {arts[i]}
              </div>
              <span className="tk-home-step__num" aria-hidden="true">
                <span>{n}</span>
              </span>
              <div className="tk-home-step__body">
                <div className="tk-home-step__label">{fill(t("tk.home.how.step"), n)}</div>
                <h3 className="tk-home-step__title">{t(`tk.home.how.s${n}.title`)}</h3>
                <p className="tk-home-step__text">{t(`tk.home.how.s${n}.text`)}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="tk-home-how__mobile-art" aria-hidden="true">
          <ShareArt t={t} featured={featured} />
        </div>
      </div>
    </section>
  );
}

function Features({ t }: { t: T }) {
  return (
    <section className="tk-section tk-section--linen tk-home-feat">
      <div className="tk-container tk-home-feat__inner">
        <SectionHead
          align="start"
          eyebrow={t("tk.home.features.eyebrow")}
          title={
            <>
              {t("tk.home.features.title_a")} <em>{t("tk.home.features.title_em")}</em>
            </>
          }
          lead={t("tk.home.features.lead")}
        />
        <ul className="tk-home-feat__list">
          {FEATURES.map(({ key, Icon }) => (
            <li key={key} className="tk-home-feat__item">
              <span className="tk-home-feat__icon" aria-hidden="true">
                <Icon size={22} strokeWidth={1.5} />
              </span>
              <div>
                <h3 className="tk-home-feat__title">{t(`tk.home.features.${key}.title`)}</h3>
                <p className="tk-home-feat__text tk-desktop-only">{t(`tk.home.features.${key}.text`)}</p>
                <p className="tk-home-feat__text tk-mobile-only">{t(`tk.home.features.${key}.short`)}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Cabinet({ t, featured }: { t: T; featured: Template }) {
  const total = DEMO_RSVP.yes + DEMO_RSVP.no + DEMO_RSVP.maybe;
  const share = (n: number) => fill(t("tk.home.demo.share"), Math.round((n / total) * 100));
  const stats: Array<[string, number, string]> = [
    [t("tk.home.demo.stat_yes"), DEMO_RSVP.yes, share(DEMO_RSVP.yes)],
    [t("tk.home.demo.stat_no"), DEMO_RSVP.no, share(DEMO_RSVP.no)],
    [t("tk.home.demo.stat_total"), total, fill(t("tk.home.demo.maybe"), DEMO_RSVP.maybe)],
  ];
  return (
    <section className="tk-section tk-home-cab">
      <div className="tk-container tk-home-cab__inner">
        <div className="tk-home-cab__intro">
          <SectionHead
            align="start"
            eyebrow={t("tk.nav.cabinet")}
            title={
              <>
                {t("tk.home.cabinet.title_a")}
                <br /> <em>{t("tk.home.cabinet.title_em")}</em>
              </>
            }
            lead={t("tk.home.cabinet.lead")}
          />
        </div>
        <ul className="tk-home-checks tk-home-cab__checks">
          {["c1", "c2", "c3"].map((k) => (
            <li key={k}>
              <Check size={20} strokeWidth={1.8} aria-hidden="true" />
              {t(`tk.home.cabinet.${k}`)}
            </li>
          ))}
        </ul>
        <div className="tk-home-cab__cta">
          <Link to="/dashboard" className="tk-btn tk-btn--secondary">
            {t("tk.home.cabinet.cta")}
            <ArrowRight size={17} strokeWidth={1.8} aria-hidden="true" />
          </Link>
        </div>

        <div className="tk-home-dash" aria-hidden="true">
          <div className="tk-home-dash__head">
            <div>
              <div className="tk-home-dash__names">{featured.names}</div>
              <div className="tk-home-dash__meta">
                {featured.date} · {featured.venue}
              </div>
            </div>
            <span className="tk-badge tk-badge--yes">
              <span className="tk-badge__dot" />
              {t("dashboard.status.published")}
            </span>
          </div>
          <div className="tk-home-dash__stats">
            {stats.map(([label, value, sub]) => (
              <div key={label} className="tk-home-dash__stat">
                <span className="tk-home-dash__label">{label}</span>
                <span className="tk-num tk-home-dash__value">{value}</span>
                <span className="tk-home-dash__sub">{sub}</span>
              </div>
            ))}
          </div>
          <div className="tk-home-dash__tabs">
            <span className="is-on">{t("tk.home.demo.responses")}</span>
            <span>{t("tk.dash.wishes")}</span>
          </div>
          <div className="tk-home-dash__rows">
            {DEMO_GUESTS.map(([g, rsvp]) => {
              const name = t(`tk.home.demo.${g}.name`);
              return (
                <div key={g} className="tk-home-dash__row">
                  <span className="tk-avatar tk-home-dash__avatar">{initials(name)}</span>
                  <strong className="tk-home-dash__who">{name}</strong>
                  <span className={`tk-badge tk-badge--${rsvp}`}>{t(`tk.home.demo.badge_${rsvp}`)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function Pricing({ t }: { t: T }) {
  const price = formatPrice(PRICE);
  const split = /^(.*\d)\s+(\D+)$/.exec(price);
  return (
    <section id="pricing" className="tk-section tk-section--linen tk-home-price">
      <div className="tk-container">
        <SectionHead
          eyebrow={t("tk.nav.pricing")}
          title={
            <>
              {t("tk.home.pricing.title_a")} <em>{t("tk.home.pricing.title_em")}</em>
            </>
          }
        />
        <div className="tk-home-price__wrap">
          <div className="tk-home-price__card">
            <div className="tk-eyebrow tk-home-price__per">
              {t(PRICE_VARIES ? "tk.home.pricing.from" : "tk.home.pricing.per")}
            </div>
            <div className="tk-home-price__amount">
              <span className="tk-num tk-home-price__num">{split ? split[1] : price}</span>
              {split && <span className="tk-home-price__unit">{split[2]}</span>}
            </div>
            <div className="tk-home-price__note">{t("tk.home.pricing.once")}</div>
            <div className="tk-home-price__rule" aria-hidden="true">
              <Sparkle size={10} />
            </div>
            <ul className="tk-home-checks tk-home-price__list">
              {["i1", "i2", "i3", "i4", "i5"].map((k) => (
                <li key={k}>
                  <Check size={20} strokeWidth={1.8} aria-hidden="true" />
                  {t(`tk.home.pricing.${k}`)}
                </li>
              ))}
            </ul>
            <Link to="/templates" className="tk-btn tk-btn--primary tk-btn--lg tk-btn--block">
              {t("tk.home.cta.choose")}
              <ArrowRight size={17} strokeWidth={1.8} aria-hidden="true" />
            </Link>
            <div className="tk-home-price__methods">
              {t(isIntlSite() ? "tk.home.pricing.methods_intl" : "tk.home.pricing.methods")}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Faq({ t }: { t: T }) {
  const [open, setOpen] = useState(0);
  const intl = isIntlSite();
  const brand = brandName();
  return (
    <section id="faq" className="tk-section tk-home-faq">
      <div className="tk-container tk-home-faq__inner">
        <div className="tk-home-faq__intro">
          <SectionHead
            align="start"
            eyebrow={t("tk.nav.faq")}
            title={
              <>
                {t("help.title_1")} <em>{t("help.title_2")}</em>
              </>
            }
            lead={t("tk.home.faq.lead")}
          />
        </div>
        <div className="tk-home-faq__cta">
          <a
            href={TELEGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="tk-btn tk-btn--secondary tk-btn--sm"
          >
            <Send size={17} strokeWidth={1.7} className="tk-home-anor" aria-hidden="true" />
            {t("tk.support.cta")}
          </a>
        </div>
        <div className="tk-home-faq__list">
          {FAQ.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} className="tk-home-faq__item">
                <h3 className="tk-home-faq__q">
                  <button
                    type="button"
                    id={`tk-faq-q-${i}`}
                    aria-expanded={isOpen}
                    aria-controls={`tk-faq-a-${i}`}
                    onClick={() => setOpen(isOpen ? -1 : i)}
                  >
                    {t(f.q)}
                    {isOpen ? (
                      <Minus size={22} strokeWidth={1.6} className="tk-home-anor" aria-hidden="true" />
                    ) : (
                      <Plus size={22} strokeWidth={1.6} className="tk-home-faq__plus" aria-hidden="true" />
                    )}
                  </button>
                </h3>
                <div
                  id={`tk-faq-a-${i}`}
                  role="region"
                  aria-labelledby={`tk-faq-q-${i}`}
                  className="tk-home-faq__a"
                  hidden={!isOpen}
                >
                  <p>{t(intl && f.aIntl ? f.aIntl : f.a).replace(/taklifla\.uz/g, brand)}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function FinalCta({ t }: { t: T }) {
  const tail = t("tk.home.final.title_b");
  return (
    <section className="tk-home-final">
      <div className="tk-container">
        <div className="tk-home-final__panel">
          <span className="tk-home-final__arch tk-home-final__arch--l" aria-hidden="true" />
          <span className="tk-home-final__arch tk-home-final__arch--r" aria-hidden="true" />
          <Sparkle size={14} className="tk-home-final__spark" />
          <h2 className="tk-h2 tk-home-final__title">
            {t("tk.home.final.title_a")} <em>{t("tk.home.final.title_em")}</em>
            {tail ? ` ${tail}` : ""}
          </h2>
          <p className="tk-lead tk-home-final__lead">{t("tk.home.final.lead")}</p>
          <Link to="/templates" className="tk-btn tk-btn--primary tk-btn--lg tk-home-final__btn">
            {t("tk.nav.create")}
            <ArrowRight size={17} strokeWidth={1.8} aria-hidden="true" />
          </Link>
        </div>
      </div>
    </section>
  );
}
